import { useState } from 'react';
import type { ProductData } from '@/mocks/products';

interface ProductInfoProps {
  product: ProductData;
}

type Tab = 'specs' | 'diferenciais' | 'aplicacoes';

const DIFERENCIAIS: Record<string, string[]> = {
  default: [
    'Controle digital de temperatura com precisão de ±1°C',
    'Estrutura em alumínio de alta resistência',
    'Sistema de pressão ajustável para diferentes espessuras',
    'Cunha de solda em aço inoxidável de alta durabilidade',
    'Painel de controle intuitivo com display digital',
    'Compatível com geomembranas PEAD, PELBD e PVC',
  ],
};

const APLICACOES: Record<string, string[]> = {
  default: [
    'Aterros sanitários e lixões',
    'Reservatórios e lagoas de contenção',
    'Barragens e diques',
    'Canais de irrigação',
    'Projetos de mineração',
    'Impermeabilização de fundações',
  ],
};

export default function ProductInfo({ product }: ProductInfoProps) {
  const [activeTab, setActiveTab] = useState<Tab>('specs');

  const waLinkInfo = `https://wa.me/554891802091?text=Ol%C3%A1!%20Tenho%20interesse%20no%20${encodeURIComponent(product.name)}.%20Poderia%20me%20passar%20mais%20informa%C3%A7%C3%B5es%3F`;

  const handleWhatsAppClick = (e: React.MouseEvent, url: string) => {
    e.preventDefault();
    if (typeof window !== 'undefined' && (window as any).gtag_report_conversion) {
      (window as any).gtag_report_conversion(url, true);
    } else {
      window.open(url, '_blank');
    }
  };

  const diferenciais = DIFERENCIAIS[product.slug] || DIFERENCIAIS.default;
  const aplicacoes = APLICACOES[product.slug] || APLICACOES.default;

  return (
    <article>
      <span className="text-green-500 text-xs uppercase tracking-widest mb-2 block">
        Máquina de Solda Geomembrana — SoldaGeo
      </span>
      <h1 className="text-5xl md:text-6xl font-black text-gray-900 uppercase leading-none mb-3">
        {product.name}
      </h1>
      <p className="text-gray-500 text-base mb-3 font-bold">{product.subtitle}</p>
      <p className="text-gray-600 text-sm leading-relaxed mb-8">{product.description}</p>

      <div className="grid grid-cols-2 gap-3 mb-8">
        {product.fullSpecs.slice(0, 4).map((spec) => (
          <div key={spec.label} className="flex items-center gap-3 bg-gray-50 rounded-xl px-4 py-3">
            <div className="w-8 h-8 flex items-center justify-center bg-green-100 rounded-lg flex-shrink-0">
              <i className={`${spec.icon} text-green-600 text-sm`} />
            </div>
            <div>
              <p className="text-gray-400 text-[10px] uppercase tracking-wide leading-none mb-0.5">{spec.label}</p>
              <p className="text-gray-900 text-sm font-black">{spec.value}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="border-b border-gray-100 mb-6">
        <div className="flex gap-0">
          {(['specs', 'diferenciais', 'aplicacoes'] as Tab[]).map((tab) => (
            <button
              key={tab}
              className={`px-5 py-3 text-xs font-black uppercase tracking-wide border-b-2 transition-all cursor-pointer whitespace-nowrap ${activeTab === tab ? 'border-green-500 text-green-600' : 'border-transparent text-gray-400 hover:text-gray-700'}`}
              onClick={() => setActiveTab(tab)}
            >
              {tab === 'specs' ? 'Especificações' : tab === 'diferenciais' ? 'Diferenciais' : 'Aplicações'}
            </button>
          ))}
        </div>
      </div>

      {activeTab === 'specs' && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {product.fullSpecs.map((spec) => (
            <div key={spec.label} className="flex items-center gap-3 py-3 border-b border-gray-50">
              <div className="w-7 h-7 flex items-center justify-center flex-shrink-0">
                <i className={`${spec.icon} text-green-500 text-base`} />
              </div>
              <div className="flex-1 flex items-center justify-between gap-2">
                <span className="text-gray-500 text-xs uppercase tracking-wide">{spec.label}</span>
                <span className="text-gray-900 text-sm font-black">{spec.value}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {activeTab === 'diferenciais' && (
        <ul className="flex flex-col gap-3">
          {diferenciais.map((item, i) => (
            <li key={i} className="flex items-start gap-3 py-2 border-b border-gray-50">
              <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 mt-0.5">
                <i className="ri-check-line text-green-500 text-base" />
              </div>
              <span className="text-gray-700 text-sm">{item}</span>
            </li>
          ))}
        </ul>
      )}

      {activeTab === 'aplicacoes' && (
        <ul className="flex flex-col gap-3">
          {aplicacoes.map((item, i) => (
            <li key={i} className="flex items-start gap-3 py-2 border-b border-gray-50">
              <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 mt-0.5">
                <i className="ri-arrow-right-s-line text-green-500 text-base" />
              </div>
              <span className="text-gray-700 text-sm">{item}</span>
            </li>
          ))}
        </ul>
      )}

      <div className="mt-10 bg-gray-900 rounded-2xl p-6">
        <p className="text-green-400 text-xs font-black uppercase tracking-widest mb-1">Pronto para comprar?</p>
        <h4 className="text-white text-2xl font-black uppercase mb-3 leading-tight">
          Fale com um especialista agora
        </h4>
        <p className="text-gray-400 text-xs mb-5">
          Nossa equipe técnica está pronta para tirar todas as suas dúvidas e fazer o melhor orçamento.
        </p>
        <a
          href={waLinkInfo}
          target="_blank"
          rel="nofollow noreferrer"
          onClick={(e) => handleWhatsAppClick(e, waLinkInfo)}
          className="flex items-center justify-center gap-2 bg-green-500 hover:bg-green-600 text-white font-black text-sm px-5 py-3 rounded-xl transition-all cursor-pointer whitespace-nowrap uppercase tracking-wide"
        >
          <i className="ri-whatsapp-line" />
          WhatsApp
        </a>
      </div>
    </article>
  );
}
